/**
 * Phoenix theme registry.
 *
 * The Phoenix design system has three orthogonal axes:
 *   - `mode`   — light or dark surface palette
 *   - `scheme` — 18 aesthetic primary palettes (never collide with the
 *                semantic colors success/warning/danger/info/required)
 *   - `feel`   — 12 neutral surface families (bg / surfaces / borders /
 *                text tone); independent of scheme and mode
 *
 * All resolve to attributes on the document root:
 *   <html data-phx-mode data-phx-scheme data-phx-feel>
 */

export type PhxMode = 'light' | 'dark';

export type PhxScheme =
  | 'indigo'
  | 'cobalt'
  | 'sky'
  | 'cyan'
  | 'teal'
  | 'mint'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'plum'
  | 'pink'
  | 'rose'
  | 'crimson'
  | 'brick'
  | 'orange'
  | 'bronze'
  | 'slate'
  | 'charcoal';

export interface PhxSchemeMeta {
  id: PhxScheme;
  label: string;
  color: string;
  darkColor: string;
}

export const PHX_SCHEMES: readonly PhxSchemeMeta[] = [
  { id: 'indigo', label: 'Indigo', color: '#4338CA', darkColor: '#818CF8' },
  { id: 'cobalt', label: 'Cobalt', color: '#1E40AF', darkColor: '#93C5FD' },
  { id: 'sky', label: 'Sky', color: '#0284C7', darkColor: '#38BDF8' },
  { id: 'cyan', label: 'Cyan', color: '#0891B2', darkColor: '#22D3EE' },
  { id: 'teal', label: 'Teal', color: '#0F766E', darkColor: '#2DD4BF' },
  { id: 'mint', label: 'Mint', color: '#10B981', darkColor: '#34D399' },
  { id: 'violet', label: 'Violet', color: '#7C3AED', darkColor: '#A78BFA' },
  { id: 'purple', label: 'Purple', color: '#9333EA', darkColor: '#C084FC' },
  { id: 'fuchsia', label: 'Fuchsia', color: '#D946EF', darkColor: '#F0ABFC' },
  { id: 'plum', label: 'Plum', color: '#A21CAF', darkColor: '#E879F9' },
  { id: 'pink', label: 'Pink', color: '#DB2777', darkColor: '#F472B6' },
  { id: 'rose', label: 'Rose', color: '#E11D48', darkColor: '#FDA4AF' },
  { id: 'crimson', label: 'Crimson', color: '#BE123C', darkColor: '#FB7185' },
  { id: 'brick', label: 'Brick', color: '#A8392B', darkColor: '#E07A5F' },
  { id: 'orange', label: 'Orange', color: '#EA580C', darkColor: '#FB923C' },
  { id: 'bronze', label: 'Bronze', color: '#92400E', darkColor: '#D97706' },
  { id: 'slate', label: 'Slate', color: '#475569', darkColor: '#94A3B8' },
  { id: 'charcoal', label: 'Charcoal', color: '#27272A', darkColor: '#71717A' },
] as const;

export type PhxFeel =
  | 'paper'
  | 'linen'
  | 'blush'
  | 'snow'
  | 'dove'
  | 'graphite'
  | 'mist'
  | 'steel'
  | 'arctic'
  | 'glacier'
  | 'sage'
  | 'lilac';

export interface PhxFeelMeta {
  id: PhxFeel;
  label: string;
  /**
   * Swatch gradient stops — selected-row surface → strongest border of the
   * family.  Deliberately more saturated than the actual page background, so
   * the tint is recognisable in a 14px swatch.
   */
  color: string;
  color2: string;
  /** Dark-mode swatch gradient stops. */
  darkColor: string;
  darkColor2: string;
}

export const PHX_FEELS: readonly PhxFeelMeta[] = [
  {
    id: 'paper',
    label: 'Paper',
    color: '#ECE5D2',
    color2: '#B0A084',
    darkColor: '#3A3329',
    darkColor2: '#6B5E50',
  },
  {
    id: 'linen',
    label: 'Linen',
    color: '#E9E8E1',
    color2: '#A9A698',
    darkColor: '#37342F',
    darkColor2: '#6B665E',
  },
  {
    id: 'blush',
    label: 'Blush',
    color: '#F0E4E1',
    color2: '#BCA098',
    darkColor: '#3C2F2B',
    darkColor2: '#715C55',
  },
  {
    id: 'snow',
    label: 'Snow',
    color: '#E9E9EB',
    color2: '#A1A1AA',
    darkColor: '#343438',
    darkColor2: '#646470',
  },
  {
    id: 'dove',
    label: 'Dove',
    color: '#E5E7EB',
    color2: '#9CA3AF',
    darkColor: '#313640',
    darkColor2: '#67707F',
  },
  {
    id: 'graphite',
    label: 'Graphite',
    color: '#D8D8DB',
    color2: '#97979F',
    darkColor: '#2C2C31',
    darkColor2: '#5A5A65',
  },
  {
    id: 'mist',
    label: 'Mist',
    color: '#E5EBF2',
    color2: '#94A3B8',
    darkColor: '#2D3744',
    darkColor2: '#5B687C',
  },
  {
    id: 'steel',
    label: 'Steel',
    color: '#D6DDE7',
    color2: '#8E9DB3',
    darkColor: '#333D4B',
    darkColor2: '#5E6D82',
  },
  {
    id: 'arctic',
    label: 'Arctic',
    color: '#DCE7F2',
    color2: '#8FA9C4',
    darkColor: '#243345',
    darkColor2: '#4F647E',
  },
  {
    id: 'glacier',
    label: 'Glacier',
    color: '#D9EAEA',
    color2: '#88B0AF',
    darkColor: '#273838',
    darkColor2: '#536E6D',
  },
  {
    id: 'sage',
    label: 'Sage',
    color: '#E0EAE1',
    color2: '#93AB96',
    darkColor: '#2B382E',
    darkColor2: '#57695B',
  },
  {
    id: 'lilac',
    label: 'Lilac',
    color: '#E9E6F3',
    color2: '#A49AC2',
    darkColor: '#342E42',
    darkColor2: '#645B7C',
  },
] as const;

export const DEFAULT_PHX_MODE: PhxMode = 'light';
export const DEFAULT_PHX_SCHEME: PhxScheme = 'cobalt';
export const DEFAULT_PHX_FEEL: PhxFeel = 'graphite';

const STORAGE_KEYS = {
  mode: 'phx-mode',
  // Bump verze klíče při změně výchozí hodnoty: stará uložená hodnota (typicky auto-zapsaná
  // z dřívějšího mountu switcheru, ne vědomá volba) by jinak nový default přebila. Bump =
  // všichni spadnou na nový default; explicitní volbu si uživatel případně nastaví znovu.
  // v2: indigo→brick, v3: brick→cobalt.
  scheme: 'phx-scheme-v3',
  // v2: paper→graphite.
  feel: 'phx-feel-v2',
} as const;

function readStored<T extends string>(key: keyof typeof STORAGE_KEYS, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const value = window.localStorage.getItem(STORAGE_KEYS[key]);
    return (value as T | null) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: keyof typeof STORAGE_KEYS, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEYS[key], value);
  } catch {
    /* ignore quota / privacy errors */
  }
}

export function applyPhxMode(mode: PhxMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-phx-mode', mode);
  writeStored('mode', mode);
}

export function applyPhxScheme(scheme: PhxScheme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-phx-scheme', scheme);
  writeStored('scheme', scheme);
}

export function applyPhxFeel(feel: PhxFeel) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-phx-feel', feel);
  writeStored('feel', feel);
}

export function readPhxMode(): PhxMode {
  const stored = readStored<PhxMode>('mode', DEFAULT_PHX_MODE);
  return stored === 'light' || stored === 'dark' ? stored : DEFAULT_PHX_MODE;
}
// Uložená hodnota nemusí být platná — např. schéma mezitím odebrané z registru
// (zinc). Neznámé hodnoty padají na default, jinak by na <html> zůstal atribut
// bez CSS bloku.
export function readPhxScheme(): PhxScheme {
  const stored = readStored<PhxScheme>('scheme', DEFAULT_PHX_SCHEME);
  return PHX_SCHEMES.some((s) => s.id === stored) ? stored : DEFAULT_PHX_SCHEME;
}
export function readPhxFeel(): PhxFeel {
  const stored = readStored<PhxFeel>('feel', DEFAULT_PHX_FEEL);
  return PHX_FEELS.some((f) => f.id === stored) ? stored : DEFAULT_PHX_FEEL;
}
